import { useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check, MessageCircle } from "lucide-react";
import { useLanguage, waLink } from "@/i18n";

const KeywordRow = ({ kw, i, t }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(kw);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {}
  };
  return (
    <motion.div
      initial={{ opacity: 0, x: -24 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, delay: (i % 5) * 0.06 }}
      data-testid={`keyword-card-${i + 1}`}
      className="group flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5 bg-slate-800/60 border border-slate-700/80 hover:border-sky-500/60 rounded-2xl px-5 sm:px-6 py-4 transition-colors"
    >
      <span className="font-mono text-sky-400 text-sm font-semibold shrink-0">{String(i + 1).padStart(2, "0")}</span>
      <p className="flex-1 font-display font-bold text-slate-100 text-base sm:text-lg tracking-tight">{kw}</p>
      <div className="flex gap-2 shrink-0">
        <button
          onClick={copy}
          data-testid={`copy-keyword-${i + 1}`}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-full border border-slate-600 text-slate-300 hover:border-sky-400 hover:text-sky-300 transition-colors"
        >
          {copied ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
          {copied ? t.keywords.copied : t.keywords.copy}
        </button>
        <a
          href={waLink(t.keywords.askMsg(kw))}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`ask-keyword-${i + 1}`}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-full bg-sky-500 text-white hover:bg-sky-400 transition-colors"
        >
          <MessageCircle size={13} /> {t.keywords.ask}
        </a>
      </div>
    </motion.div>
  );
};

export default function Keywords() {
  const { t } = useLanguage();
  return (
    <section id="kata-kunci" className="py-24 sm:py-32 bg-[#070E1A] blueprint-grid-dark" data-testid="keywords-section">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-2xl mx-auto"
        >
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-sky-400 font-semibold">{t.keywords.eyebrow}</p>
          <h2 className="mt-4 font-display font-extrabold tracking-tight text-white text-2xl sm:text-3xl lg:text-4xl">{t.keywords.title}</h2>
          <p className="mt-4 text-slate-400 text-base sm:text-lg">{t.keywords.sub}</p>
        </motion.div>
        <div className="mt-12 space-y-3">
          {t.keywords.list.map((kw, i) => (
            <KeywordRow key={kw} kw={kw} i={i} t={t} />
          ))}
        </div>
      </div>
    </section>
  );
}
